import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Store as StoreIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Restaurant as RestaurantIcon,
  AttachMoney as SalesIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const getTranslatedText = (translationObj, defaultLang = 'ja') => {
  if (!translationObj) return '';
  
  if (typeof translationObj !== 'object') return translationObj;
  
  return translationObj[defaultLang] || translationObj.ja || translationObj.en || translationObj.zh || '';
};

const DashboardCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: "50%",
              padding: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Grid>
        <Grid item xs>
          <Typography variant="h6" component="div">
            {value}
          </Typography>
          <Typography color="textSecondary">{title}</Typography>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStores: 0,
    totalStaff: 0,
    totalProducts: 0,
    totalSales: 0,
    recentStores: [],
    topProducts: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log("ダッシュボード統計を取得中...");
        const response = await api.get("/api/brand/dashboard/stats");
        const data = response.data;

        console.log("API レスポンス:", data);

        // APIからエラーメッセージが返された場合
        if (data.error) {
          console.warn("API returned error:", data.error);
          setError(data.error);
        }

        // APIレスポンス形式を正規化
        setStats({
          totalStores: data.totalStores ?? data.metrics?.stores ?? 0,
          totalStaff: data.totalStaff ?? data.metrics?.staff ?? 0,
          totalProducts: data.totalProducts ?? data.metrics?.products ?? 0,
          totalSales: data.totalSales ?? 0,
          recentStores: data.recentStores ?? [],
          topProducts: data.topProducts ?? [],
        });
      } catch (error) {
        console.error("統計情報の取得に失敗しました:", error);
        console.error("Error details:", error.response?.data);

        // ユーザーに表示するエラーメッセージ
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.error ||
                            error.message ||
                            "統計情報の取得に失敗しました";
        setError(errorMessage);

        // エラー時もデフォルト値を設定（空白ページを防ぐ）
        setStats({
          totalStores: 0,
          totalStaff: 0,
          totalProducts: 0,
          totalSales: 0,
          recentStores: [],
          topProducts: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statCards = [
    {
      title: "店舗数",
      value: stats.totalStores,
      icon: <StoreIcon sx={{ fontSize: 40 }} />,
      color: "#1976d2",
    },
    {
      title: "スタッフ数",
      value: stats.totalStaff,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#2e7d32",
    },
    {
      title: "商品数",
      value: stats.totalProducts,
      icon: <RestaurantIcon sx={{ fontSize: 40 }} />,
      color: "#ed6c02",
    },
    {
      title: "今月の売上",
      value: `¥${(stats.totalSales || 0).toLocaleString()}`,
      icon: <SalesIcon sx={{ fontSize: 40 }} />,
      color: "#f57c00",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        ダッシュボード
      </Typography>

      {/* エラーメッセージの表示 */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* サマリーカード */}
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <DashboardCard
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
            />
          </Grid>
        ))}

        {/* 店舗一覧 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="店舗一覧"
              action={
                <Button
                  size="small"
                  endIcon={<TrendingUpIcon />}
                  onClick={() => navigate("/brand-admin/stores")}
                >
                  すべて表示
                </Button>
              }
            />
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>店舗名</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>スタッフ数</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>今月の売上</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>ステータス</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(stats.recentStores || []).map((store) => (
                      <TableRow key={store.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{store.name}</TableCell>
                        <TableCell>{store.staffCount || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          ¥{(store.monthlySales || 0).toLocaleString()}
                        </TableCell>
                        <TableCell>{store.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 人気商品 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="人気商品ランキング"
              action={
                <Button
                  size="small"
                  endIcon={<RestaurantIcon />}
                  onClick={() => navigate("/brand-admin/menu")}
                >
                  メニュー管理
                </Button>
              }
            />
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>商品名</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>カテゴリ</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>販売数</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>売上</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(stats.topProducts || []).map((product) => (
                      <TableRow key={product.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{getTranslatedText(product.name)}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>{product.salesCount || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          ¥{(product.salesAmount || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 在庫アラート セクションは削除されました */}
      </Grid>
    </Container>
  );
};

export default Dashboard;
